import { reverse } from "@reactivated";

import {
    EmailVerificationSendResponse,
    EmailVerificationSendStatus,
    EmailVerificationVerifyResponse,
} from "../models/emailVerification";
import { check } from "../models/utils";
import { CSRF_HEADER, fetchJson, getCSRFToken } from "../utils/ajax";

/**
 * Send an email-ownership verification code to the given address (recorded
 * against the current user account).
 *
 * Throws HttpError with status 429 when the resend cooldown or send rate
 * limits are hit.
 */
export const sendVerificationCode = async (
    email: string,
): Promise<EmailVerificationSendStatus> => {
    const body = await fetchJson(reverse("emailverification:send"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify({ email: email }),
    });
    return check(EmailVerificationSendResponse.decode(body)).status;
};

/**
 * Verify a code previously sent by sendVerificationCode.
 *
 * Throws HttpError with status 400 when the code is wrong, expired, or the
 * attempt limit has been exhausted.
 */
export const verifyEmailCode = async (
    email: string,
    code: string,
): Promise<EmailVerificationVerifyResponse> => {
    const body = await fetchJson(reverse("emailverification:verify"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify({ email: email, code: code }),
    });
    return check(EmailVerificationVerifyResponse.decode(body));
};
